import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const _cache = {};

// Einzige Quelle für AGB + Widerrufsbelehrung: GET /api/legal/agb?lang=… (backend/legal/agb*.json).
// lang='de' erzwingt die rechtlich maßgebliche deutsche Fassung (z. B. in der Mandantenerfassung).
export const useLegalAgb = (lang) => {
  const { i18n } = useTranslation();
  const code = (lang || i18n.language || 'de').slice(0, 2);
  const [agb, setAgb] = useState(_cache[code] || null);
  useEffect(() => {
    if (_cache[code]) { setAgb(_cache[code]); return; }
    axios.get(`${API}/legal/agb`, { params: { lang: code } })
      .then((r) => { _cache[code] = r.data; setAgb(r.data); })
      .catch((e) => console.warn('[legal] AGB konnten nicht geladen werden:', e?.message));
  }, [code]);
  return agb;
};

export const AGB_PDF_URL = `${API}/legal/agb.pdf`;
export const WIDERRUF_PDF_URL = `${API}/legal/widerrufsformular`;

// Datenschutz / Impressum: GET /api/legal/<name>?lang=… (backend/legal/<name>*.json)
export const useLegalDoc = (name, lang) => {
  const { i18n } = useTranslation();
  const code = (lang || i18n.language || 'de').slice(0, 2);
  const key = `${name}:${code}`;
  const [doc, setDoc] = useState(_cache[key] || null);
  useEffect(() => {
    if (_cache[key]) { setDoc(_cache[key]); return; }
    axios.get(`${API}/legal/${name}`, { params: { lang: code } })
      .then((r) => { _cache[key] = r.data; setDoc(r.data); })
      .catch((e) => console.warn(`[legal] ${name} konnte nicht geladen werden:`, e?.message));
  }, [key, name, code]);
  return doc;
};

// Inline-Markdown der Legal-JSONs: [text](url), **fett**, \n → <br>
const INLINE_RE = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\n)/g;
export const LegalInline = ({ text }) => (
  <>
    {text.split(INLINE_RE).filter(Boolean).map((part, i) => {
      if (part === '\n') return <br key={i} />;
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        const ext = !link[2].startsWith('mailto:');
        return (
          <a key={i} href={link[2]} className="text-[#b8893d] underline break-all" {...(ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {link[1]}
          </a>
        );
      }
      if (part.startsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
      return <React.Fragment key={i}>{part}</React.Fragment>;
    })}
  </>
);

const H2 = 'font-serif text-2xl text-[#1B3A5C] mt-10 mb-3';
const H3 = 'font-serif text-lg text-[#1B3A5C] mt-6 mb-2';
const P = 'text-[#34536b] text-sm leading-relaxed';

export const LegalSections = ({ sections }) => sections.map((sec) => (
  <div key={sec.title}>
    {sec.level === 3 ? <h3 className={H3}>{sec.title}</h3> : <h2 className={H2}>{sec.title}</h2>}
    {sec.paras.map((p) => <p key={p.slice(0, 40)} className={`${P} mb-3`}><LegalInline text={p} /></p>)}
    {sec.box && (
      <div className="mt-4 p-4 border-l-4 border-[#b8893d] bg-[#fbf7f0]">
        <p className="text-[#1B3A5C] text-sm leading-relaxed"><LegalInline text={sec.box} /></p>
      </div>
    )}
  </div>
));

// Hinweis-Box „Unverbindliche Übersetzung" + Umschalter – identisch zu /agb
export const TranslationNotice = ({ doc, forceDe, setForceDe, testid }) => {
  const { i18n } = useTranslation();
  const siteLangIsDe = (i18n.language || 'de').startsWith('de');
  const isTranslation = Boolean(doc?.notice) && doc?.lang !== 'de';
  if (isTranslation) {
    return (
      <div className="mb-8 p-4 bg-[#fbf7f0] border-l-4 border-[#b8893d] text-sm text-[#34536b] leading-relaxed" data-testid={`${testid}-translation-notice`}>
        <p>{doc.notice}</p>
        <p className="mt-2 text-xs">
          <span className="text-[#7a8a99]">DE:</span> Unverbindliche Übersetzung – rechtlich maßgeblich ist ausschließlich die deutsche Fassung.{' '}
          <button type="button" onClick={() => setForceDe(true)} className="underline text-[#b8893d] hover:text-[#8f6a2b]" data-testid={`${testid}-show-german`}>Deutsche Fassung anzeigen</button>
        </p>
      </div>
    );
  }
  if (forceDe && !siteLangIsDe) {
    return (
      <p className="mb-6 text-xs text-[#7a8a99]">
        Rechtlich maßgebliche deutsche Fassung.{' '}
        <button type="button" onClick={() => setForceDe(false)} className="underline text-[#b8893d]" data-testid={`${testid}-show-translation`}>Übersetzung anzeigen</button>
      </p>
    );
  }
  return null;
};

const box = 'text-[13px] text-[#34536b] leading-relaxed bg-[#fbf7f0] border-l-4 border-[#b8893d] px-4 py-4 mb-4 space-y-3 max-h-[420px] overflow-y-auto';

export const AgbVolltext = ({ agb }) => {
  if (!agb) return <p className="text-xs text-[#34536b] italic mb-3">AGB werden geladen …</p>;
  return (
    <div className={box} data-testid="agb-volltext">
      <p className="text-[11px] text-[#7a8a99]">{agb.kopf.firma} · {agb.kopf.adresse} · {agb.kopf.register} · Stand: {agb.stand}</p>
      {agb.sections.map((s) => (
        <div key={s.title}>
          <p className="font-semibold text-[#1B3A5C] mt-2">{s.title}</p>
          {s.paras.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
        </div>
      ))}
    </div>
  );
};

export const WiderrufVolltext = ({ agb }) => {
  if (!agb) return <p className="text-xs text-[#34536b] italic mb-3">Belehrung wird geladen …</p>;
  const w = agb.widerruf;
  return (
    <div className={box} data-testid="widerruf-belehrung-text">
      <p>{w.intro}</p>
      {w.blocks.map((b) => (
        <div key={b.heading}>
          <p className="font-semibold text-[#1B3A5C]">{b.heading}</p>
          {b.paras.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
        </div>
      ))}
      <p className="text-[11px] italic text-[#7a8a99]">— Ende der Widerrufsbelehrung —</p>
    </div>
  );
};
